import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

import { authApi, vertaalApiFout } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loginSchema, type LoginValues } from "@/lib/validators";

export function LoginPagina() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const setPending2FA = useAuthStore((s) => s.setPending2FA);
  const [bezig, setBezig] = useState(false);

  // ProtectedRoute stuurt uitgelogde bezoekers hierheen mét de oorspronkelijke
  // locatie: na inloggen (of via een ander tabblad) landen ze weer op de
  // gebookmarkte of gepushte pagina in plaats van op het Dashboard.
  const vandaan = (location.state as { from?: Location } | null)?.from;

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", wachtwoord: "" },
  });

  // Multi-tab (27-07): logt een ander tabblad in, dan komen de tokens
  // via storage-sync binnen — dit tabblad hoort dan direct het portaal
  // in te gaan in plaats van op het inlogscherm te blijven staan.
  if (accessToken) {
    return <Navigate to={vandaan ?? "/"} replace />;
  }

  async function onSubmit(values: LoginValues) {
    setBezig(true);
    try {
      const resp = await authApi.login(values);
      setPending2FA(resp.tweefa_pending_token);
      navigate("/login/2fa", { state: { from: vandaan } });
    } catch (fout) {
      toast.error(vertaalApiFout(fout, t("auth.login_mislukt")));
    } finally {
      setBezig(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo size={56} />
        </div>
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight">
            VoerOnline Klantenservice
          </h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            {t("auth.inloggen")}
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="bg-card border rounded-xl shadow-sm p-6 space-y-4"
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder={t("auth.email_placeholder")}
              aria-invalid={Boolean(form.formState.errors.email)}
              {...form.register("email")}
            />
            {form.formState.errors.email && (
              <p
                className="text-[12.5px] text-destructive"
                role="alert"
                aria-live="polite"
              >
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wachtwoord">{t("auth.wachtwoord")}</Label>
            <Input
              id="wachtwoord"
              type="password"
              autoComplete="current-password"
              placeholder={t("auth.wachtwoord_placeholder")}
              aria-invalid={Boolean(form.formState.errors.wachtwoord)}
              {...form.register("wachtwoord")}
            />
            {form.formState.errors.wachtwoord && (
              <p
                className="text-[12.5px] text-destructive"
                role="alert"
                aria-live="polite"
              >
                {form.formState.errors.wachtwoord.message}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={bezig}>
            {bezig && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("auth.inloggen")}
          </Button>

          <Link
            to="/wachtwoord-vergeten"
            className="block w-full text-center text-[12.5px] text-muted-foreground hover:text-foreground"
          >
            {t("auth.wachtwoord_vergeten")}
          </Link>
        </form>

        <p className="flex items-center gap-1.5 justify-center text-[12px] text-muted-foreground mt-6">
          <ShieldCheck className="h-3.5 w-3.5" />
          Beveiligd met TLS 1.3 en 2-factor verplicht
        </p>
      </div>
    </div>
  );
}
